import { HotelBookingMethod } from '../../globalModels/common/hotelBookingMethod.ts';
import { CommunicationType } from '../../globalModels/common/communicationType.ts';
import { queryStringArray, roots } from './utils.ts';

export const ActivityEndpoints = {
  activityStatus: (activityId: number) => `/api/activities/${activityId}/status`,
  addQuickActionNote: (activityId: number) => `/api/activities/${activityId}/quickAction`,

  communicationRecords: {
    post: (activityId: number) => `${roots.activityNew('communicationRecords', activityId)}`,
    get: (activityId: number, includeRelated: boolean = false) =>
      `${roots.activityNew('communicationRecords', activityId)}?includeRelated=${includeRelated}`,
    split: (activityId: number, recordId: number, deleted: boolean) =>
      `${roots.activityNew('communicationRecords', activityId)}/${recordId}/split?deleted=${deleted}`,
    markRead: (activityId: number, recordId: number) =>
      `${roots.activityNew('communicationRecords', activityId)}/${recordId}/read`,
    delete: (activityId: number, recordId: number) =>
      `${roots.activityNew('communicationRecords', activityId)}/${recordId}`
  },
  activityAdmin: {
    removeCancellation: (activityId: number) => `${roots.activityNew('admin', activityId)}/RemoveCancellation`,
    removeItinerary: (activityId: number) => `${roots.activityNew('admin', activityId)}/RemoveItinerary`
  },
  cancel: (activityId: number) => `${roots.activityNew('cancel', activityId)}`,
  edit: {
    get: (activityId: number) => roots.activityNew('edit', activityId),
    put: (activityId: number | undefined) => roots.activityNew('edit', activityId),
    post: () => roots.activityNew('edit', undefined)
  },
  assign: {
    post: () => `${roots.activityNew('assignUser', undefined)}`
  },
  inboxManager: {
    activity: {
      add: (activityId: number) => `/api/InboxManagerPriorityList/activity/${activityId}`,
      delete: (activityId: number) => `/api/InboxManagerPriorityList/activity/${activityId}`
    }
  },
  utility: {
    recordLocatorCheck: (reservationNumber: string) =>
      `/api/activities/utility/ReclocCheck?recordLocator=${reservationNumber}`,
    getRelatedActivityIds: (id: number) => `/api/activities/${id}/utility/related`,

    sendToFolio: (activityId: number) => `${roots.activityNew('utility', activityId)}/SendToFolio`,
    confirmSertifi: (activityId: number) => `${roots.activityNew('utility', activityId)}/confirmSertifi`,
    getEmailBody: (activityId: number) => `${roots.activityNew('utility', activityId)}/EmailBody`,
    sendToGroups: (activityId: number) => `${roots.activityNew('utility', activityId)}/SendToGroups`,
    duplicate: (activityId: number, count: number) => `${roots.activityNew('utility', activityId)}/duplicate/${count}`,
    delete: (activityId: number, reason: string) =>
      `${roots.activityNew('utility', activityId)}/delete?reason=${reason}`,
    split: (activityId: number) => `${roots.activityNew('utility', activityId)}/split`,
    merged: (destinationActivityId: number, sourceActivityIds: number[], isFolio: boolean) =>
      `${roots.activityNew('utility', destinationActivityId)}/merge?${queryStringArray('toMerge', sourceActivityIds)}isFolio=${isFolio}`
  },
  clientCommunication: {
    sendClient: (activityId: number) => `${roots.activityNew('SendCommunication', activityId)}/client`,
    sendHotel: (activityId: number) => `${roots.activityNew('SendCommunication', activityId)}/hotel`
  },
  travellers: {
    checkName: (companyId: number, activityId?: number) =>
      `${roots.activityNew('travellers', activityId)}/${companyId}/namecheck`,
    createUnverified: (activityId: number | undefined, companyId: number) =>
      `${roots.activityNew('travellers', activityId)}/${companyId}`,
    createGroupsOnly: (companyId: number) => `${roots.activityNew('travellers', undefined)}/groups-only/${companyId}`,
    duplicateBooking: (
      travellerIds: number[],
      checkInDate: string,
      checkOutDate: string,
      existingActivityId: number | undefined
    ) =>
      `${roots.activityNew('travellers', existingActivityId)}/duplicatebooking?${queryStringArray('travellerIds', travellerIds)}checkInDate=${checkInDate}&checkOutDate=${checkOutDate}`
  },
  travellersAndPaymentInfo: {
    loadInitialData: (travellerIds: number[], companyId: number, activityId: number) =>
      `/api/activities/travellersandpaymentinfo/loadInitialData/${companyId}/${activityId}?${queryStringArray('travellerIds', travellerIds)}`,
    savePrimaryTravellerId: () => `/api/activities/travellersandpaymentinfo/savePrimaryTravellerId`,
    savePrimaryTravellerLoyaltyAccounts: () =>
      `/api/activities/travellersandpaymentinfo/savePrimaryTravellerLoyaltyAccounts`
  },
  reportingRequirements: {
    get: (activityId: number) => roots.activityNew('ReportingRequirements', activityId),
    getNew: (companyId: number, travellerIds: number[]) =>
      `${roots.activityNew('ReportingRequirements', undefined)}?companyId=${companyId}&${queryStringArray('travellerIds', travellerIds)}`,
    put: (activityId: number) => roots.activityNew('ReportingRequirements', activityId)
  },
  paymentCard: {
    transactions: (activityId: number, cardId: string) =>
      `/api/activities/${activityId}/paymentCard/transactions?cardId=${cardId}`,
    createVcc: (activityId: number) => `${roots.activityNew('paymentCard', activityId)}/vcc`,
    createCard: (activityId: number) => `${roots.activityNew('paymentCard', activityId)}`,
    addOtherCard: (activityId: number, cardId: number) =>
      `${roots.activityNew('paymentCard', activityId)}/corporate/${cardId}`,
    overrideMcc: (activityId: number) => `${roots.activityNew('paymentCard', activityId)}/overrideMcc`,
    resendCorporatePaymentEmail: (activityId: number) =>
      `${roots.activityNew('paymentCard', activityId)}/resendCorporatePaymentEmail`
  },
  itinerary: {
    get: (activityId: number, rateId: number | undefined) =>
      rateId
        ? `${roots.activityNew('Itinerary', activityId)}?rateId=${rateId}`
        : roots.activityNew('Itinerary', activityId),
    put: (activityId: number) => `${roots.activityNew('Itinerary', activityId)}`,

    getTaxes: (activityId: number) => `${roots.activityNew('Itinerary', activityId)}/taxes`,
    getFinancial: (activityId: number) => `${roots.activityNew('Itinerary', activityId)}/financial`,

    book: (activityId: number, rateId: number, bookingMethod: HotelBookingMethod, allowDuplicate: boolean) => {
      switch (bookingMethod) {
        case HotelBookingMethod.Manual:
          return `${roots.activityNew('book', activityId)}/${rateId}/manual?allowDuplicateBooking=${allowDuplicate}`;
        case HotelBookingMethod.AgentBookingPassive:
          return `${roots.activityNew('book', activityId)}/${rateId}/passive?allowDuplicateBooking=${allowDuplicate}`;
        case HotelBookingMethod.AgentBookingActive:
          return `${roots.activityNew('book', activityId)}/${rateId}/active?allowDuplicateBooking=${allowDuplicate}`;
        default:
          throw new Error('Invalid hotel booking method');
      }
    },
    preCheck: (activityId: number, rateId: number) => `${roots.activityNew('book', activityId)}/${rateId}/check`,
    preview: (activityId: number, communicationType: CommunicationType, rateId?: number, isCancel?: boolean) => {
      return rateId
        ? `${roots.activityNew('Itinerary', activityId)}/preview/${communicationType}/${rateId}`
        : `${roots.activityNew('Itinerary', activityId)}/preview/${communicationType}/?isCancel=${isCancel == undefined ? false : isCancel}`;
    }
  },
  hotelRates: {
    get: (activityId: number) => roots.activityNew('Rates', activityId),
    put: (activityId: number, rateId: number) => `${roots.activityNew('Rates', activityId)}/${rateId}`,
    daily: {
      put: (activityId: number, rateId: number) => roots.activityNew(`rates/daily/${rateId}`, activityId)
    },
    copy: (activityId: number, rateId: number) => `${roots.activityNew('rates', activityId)}/copy/${rateId}`,
    delete: (activityId: number, rateId: number) => `${roots.activityNew('rates', activityId)}/delete/${rateId}`,
    post: (activityId: number) => roots.activityNew('Rates', activityId)
  },
  hotelSearch: {
    searchInformation: (activityId: number) => `${roots.activityNew('hotelSearch', activityId)}/searchInformation`,
    search: (activityId: number) => `${roots.activityNew('hotelSearch', activityId)}`,
    rules: (activityId: number, flsqNumber: number, rateKey: string) =>
      `${roots.activityNew('hotelSearch', activityId)}/rules/${rateKey}?flsqNumber=${flsqNumber}`,
    createUnverified: (activityId: number, forQuote: boolean) =>
      `${roots.activityNew('hotelSearch', activityId)}/hotel/?forQuote=${forQuote}`,
    verifyRates: (flsqNumber: number | undefined) => {
      const url = roots.activityNew('hotelSearch', undefined) + `/verifyRates`;
      if (flsqNumber) {
        return `${url}?flsqNumber=${flsqNumber}`;
      }
      return url;
    },
    runMissingLatLngJob: `/api/activities/hotelsearch/RunMissingLatLngJob`
  },
  information: {
    company: (companyId: number) => `${roots.activityNew('information', undefined)}/company/${companyId}`,
    hotels: (hotelIds: number[]) =>
      `${roots.activityNew('information', undefined)}/hotels?${queryStringArray('hotelIds', hotelIds)}`,
    travellers: (travellerIds: number[]) =>
      `${roots.activityNew('information', undefined)}/travellers?${queryStringArray('travellerIds', travellerIds)}`,
    travellerSearch: `${roots.activityNew('information', undefined)}/travellersearch`
  },

  quote: {
    sendQuote: (activityId: number) => `${roots.activityNew('quote', activityId)}/send`,
    preview: (activityId: number, communicationType: CommunicationType) =>
      `${roots.activityNew('quote', activityId)}/preview/${communicationType}`
  },
  approvals: {
    activity: (activityId: number) => `/api/activities/${activityId}/approvals`,
    approve: (externalId: string, useNewVersion?: boolean) =>
      `/api/approvals/${externalId}/approve${useNewVersion ? '?useNewVersion=true' : ''}`,
    overrideApprove: (activityId: number, externalId: string) =>
      `/api/activities/${activityId}/approvals/${externalId}/override-approve`,
    decline: (externalId: string) => `/api/approvals/${externalId}/decline`,
    hold: (externalId: string) => `/api/approvals/${externalId}/hold`,
    resumeHold: (activityId: number) => `/api/activities/${activityId}/approvals/resume-hold`,
    forceNext: (activityId: number) => `/api/activities/${activityId}/approvals/force-next`,
    requestPassive: (activityId: number, rateId: number) =>
      `/api/activities/${activityId}/approvals/request-passive/${rateId}`
  }
};
